import { FC, useRef, useState } from "react";

import { Box, HStack } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { useTranslation } from "next-i18next";

import { FullWidthVStack } from "@components/common";
import TextInput from "@components/TextInput";
import { ORDER_FIELDS_MAX_LENGTHS } from "@modules/order/constants";

import StreetInput from "./components/StreetInput";
import ZipCodeInput from "./components/ZipCodeInput";
import type { TAddressDetails } from "./types";

type TAddressInputsProps = {
  id: string;
  countryCode: string;
  streetFieldName: string;
  houseNoFieldName: string;
  flatNoFieldName: string;
  cityFieldName: string;
  zipCodeFieldName: string;
  onError?: () => void;
  isAddressSelectedFromAddressBook: boolean;
};

const AddressInputs: FC<TAddressInputsProps> = ({
    id,
    countryCode,
    streetFieldName,
    houseNoFieldName,
    flatNoFieldName,
    cityFieldName,
    zipCodeFieldName,
    onError,
    isAddressSelectedFromAddressBook
  }) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  const [isUpdateFromZipCode, setIsUpdateFromZipCode] = useState(true);

  const handleAddressSelected = (addressItem: TAddressDetails) => {
    const { street, city, houseNumber, postalCode } = addressItem;
    if (street) {
      setFieldValue(streetFieldName, street);
    }
    if (houseNumber) {
      setFieldValue(houseNoFieldName, houseNumber);
    }
    if (city) {
      setFieldValue(cityFieldName, city);
    }
    if (postalCode) {
      setFieldValue(zipCodeFieldName, postalCode);
    }
    setIsUpdateFromZipCode(false);
  };

  const handleManualEdit = () => {
    setIsUpdateFromZipCode(true);
  };

  return (
    <FullWidthVStack spacing="1rem">
      <Box data-field={streetFieldName}>
        <StreetInput
          id={`${id}-street-field`}
          name={streetFieldName}
          countryCode={countryCode}
          onAddressSelected={handleAddressSelected}
          onError={onError}
        />
      </Box>
      <HStack>
        <Box flex="1" data-field={houseNoFieldName}>
          <TextInput
            name={houseNoFieldName}
            label={t("address_fields-house_no")}
            id={`${id}-house-no-field`}
            onChange={handleManualEdit}
            maxLength={ORDER_FIELDS_MAX_LENGTHS.houseNo}
          />
        </Box>
        <Box flex="1" data-field={flatNoFieldName}>
          <TextInput
            name={flatNoFieldName}
            label={t("address_fields-flat_no")}
            id={`${id}-flat-no-field`}
            maxLength={ORDER_FIELDS_MAX_LENGTHS.flatNo}
          />
        </Box>
      </HStack>
      <Box data-field={cityFieldName}>
        <TextInput
          name={cityFieldName}
          label={t("address_fields-city")}
          id={`${id}-city-field`}
          maxLength={ORDER_FIELDS_MAX_LENGTHS.city}
          onChange={handleManualEdit}
        />
      </Box>
      <Box data-field={zipCodeFieldName}>
        <ZipCodeInput
          name={zipCodeFieldName}
          id={`${id}-zipcode-field`}
          streetFieldName={streetFieldName}
          houseNoFieldName={houseNoFieldName}
          cityFieldName={cityFieldName}
          countryCode={countryCode}
          isAddressSelectedFromAddressBook={isAddressSelectedFromAddressBook}
          isUpdateFromZipCode={isUpdateFromZipCode}
        />
      </Box>
    </FullWidthVStack>
  );
};
export default AddressInputs;